import {useEffect, useMemo, useRef, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {useSelector} from 'react-redux';
import {
  SearchOutlined,
  CloseOutlined,
  StarOutlined,
  StarFilled,
} from '@ant-design/icons';
import {AnimatePresence, motion} from 'framer-motion';
import styles from './dashboardSearch.module.scss';
import {
  buildDashboardSearchIndex,
  searchDashboard,
} from 'utils/dashboardSearchIndex';
import {useFavoritesStore} from '../../zustand/favoritesStore';

/**
 * Command-palette style search for the admin dashboard.
 *
 * Indexes every tab / sub-tab that navigates without a dynamic id (derived
 * straight from ADMIN_TABS, so results always point at real destinations) and
 * lets the admin jump to any of them. Opening it dims + blurs the background.
 */
const DashboardSearch = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef(null);

  const userId = useSelector(state => state?.auth?.userData?.user?.id);
  const roleName = useSelector(
    state => state?.auth?.userData?.user?.activeAdminTab?.name,
  );
  const isSuperAdmin = roleName === 'SUPERADMIN';
  const favoritesByUser = useFavoritesStore(state => state.favoritesByUser);
  const toggleFavorite = useFavoritesStore(state => state.toggleFavorite);
  const favIds = favoritesByUser[userId] || [];

  const index = useMemo(() => buildDashboardSearchIndex(), []);
  const results = useMemo(() => searchDashboard(index, query), [index, query]);

  // Reset the highlighted row whenever the result set changes.
  useEffect(() => {
    setActiveIndex(0);
  }, [query]);

  // Global Ctrl/⌘+K opens the palette from anywhere in the dashboard.
  useEffect(() => {
    if (!isSuperAdmin) return undefined;
    const onKeyDown = e => {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        setOpen(prev => !prev);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isSuperAdmin]);

  // Focus the field and lock body scroll while the palette is open.
  useEffect(() => {
    if (open) {
      const id = window.setTimeout(() => inputRef.current?.focus(), 10);
      document.body.style.overflow = 'hidden';
      return () => {
        window.clearTimeout(id);
        document.body.style.overflow = '';
      };
    }
    return undefined;
  }, [open]);

  const closePalette = () => {
    setOpen(false);
    setQuery('');
    setActiveIndex(0);
  };

  const goTo = entry => {
    if (!entry) return;
    navigate(entry.path);
    closePalette();
  };

  const handleKeyDown = e => {
    if (e.key === 'Escape') {
      closePalette();
      return;
    }
    if (!results.length) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex(prev => (prev + 1) % results.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex(prev => (prev - 1 + results.length) % results.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      goTo(results[activeIndex]);
    }
  };

  // Defense in depth: the search + favorites are super-admin only, even if this
  // component is ever mounted somewhere other than the gated header.
  if (!isSuperAdmin) return null;

  return (
    <>
      <button
        type="button"
        className={styles.trigger}
        onClick={() => setOpen(true)}
        aria-label="Search dashboard tabs">
        <SearchOutlined className={styles.triggerIcon} />
        <span className={styles.triggerText}>Search tabs, pages…</span>
        <span className={styles.shortcut}>Ctrl / ⌘ K</span>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            className={styles.overlay}
            initial={{opacity: 0}}
            animate={{opacity: 1}}
            exit={{opacity: 0}}
            transition={{duration: 0.2}}
            onMouseDown={closePalette}>
            <motion.div
              className={styles.panel}
              initial={{opacity: 0, y: -16, scale: 0.98}}
              animate={{opacity: 1, y: 0, scale: 1}}
              exit={{opacity: 0, y: -16, scale: 0.98}}
              transition={{duration: 0.2, ease: 'easeOut'}}
              onMouseDown={e => e.stopPropagation()}>
              <div className={styles.searchRow}>
                <SearchOutlined className={styles.searchIcon} />
                <input
                  ref={inputRef}
                  className={styles.input}
                  value={query}
                  onChange={e => setQuery(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Search for a tab or page…"
                />
                <button
                  type="button"
                  className={styles.closeBtn}
                  onClick={closePalette}
                  aria-label="Close search">
                  <CloseOutlined />
                </button>
              </div>

              <div className={styles.results}>
                {!query.trim() && (
                  <p className={styles.hint}>
                    Start typing to find any dashboard tab or sub-tab.
                  </p>
                )}

                {query.trim() && results.length === 0 && (
                  <p className={styles.empty}>
                    No tab matches “{query.trim()}”.
                  </p>
                )}

                {results.map((entry, i) => (
                  <motion.button
                    type="button"
                    key={entry.id}
                    className={styles.result}
                    initial={{opacity: 0, y: 6}}
                    animate={{opacity: 1, y: 0}}
                    transition={{
                      duration: 0.18,
                      delay: Math.min(i * 0.03, 0.25),
                    }}
                    onMouseEnter={() => setActiveIndex(i)}
                    onClick={() => goTo(entry)}>
                    {i === activeIndex && (
                      <motion.span
                        layoutId="dashboardSearchHighlight"
                        className={styles.activeHighlight}
                        transition={{
                          type: 'spring',
                          stiffness: 500,
                          damping: 40,
                        }}
                      />
                    )}
                    <span className={styles.resultLabel}>{entry.label}</span>
                    <span className={styles.resultMeta}>
                      {entry.parent && (
                        <span className={styles.resultParent}>
                          {entry.parent}
                        </span>
                      )}
                      <span
                        role="button"
                        tabIndex={-1}
                        aria-label={
                          favIds.includes(entry.id)
                            ? 'Remove from favorites'
                            : 'Add to favorites'
                        }
                        className={`${styles.star} ${
                          favIds.includes(entry.id) ? styles.starActive : ''
                        }`}
                        onClick={e => {
                          e.stopPropagation();
                          toggleFavorite(userId, entry.id);
                        }}>
                        {favIds.includes(entry.id) ? (
                          <StarFilled />
                        ) : (
                          <StarOutlined />
                        )}
                      </span>
                    </span>
                  </motion.button>
                ))}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default DashboardSearch;
